// 가입 반려 화면 — 반려 사유 · 회사 정보(입점 칸 재사용)를 고쳐 [다시 신청] 또는 24시간 안내 · 성공하면 셸을 다시 읽어 레이아웃이 승인 대기 안내로.

import { FormEvent, useEffect, useState, useTransition } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import { reapplyInfo, type ReapplyInfoResult } from "@/api/actions/onboarding";
import { OrgBlockedNotice } from "@/components/org-access-notice";
import CompanyFields from "./company-fields";
import type { WizardValues } from "./onboarding-wizard-state";
import { REAPPLY_FAILED_MESSAGE, REAPPLY_LOAD_FAILED_MESSAGE, REAPPLY_TITLE, reapplyErrorOf, reapplyInputOf, reapplyValuesOf, sendReapply } from "./reapply-state";

interface ViewProps {
  // 셸·오류가 알려 준 사유 — 서버 정보가 오면 그쪽이 우선.
  reason: string | null;
  // null = 불러오는 중.
  info: ReapplyInfoResult | null;
  values: WizardValues | null;
  error: string | null;
  pending: boolean;
  onChange: (patch: Partial<WizardValues>) => void;
  onSubmit: () => void;
  onLogout?: () => void;
}

export const OrgRejectedView = ({ reason, info, values, error, pending, onChange, onSubmit, onLogout }: ViewProps) => {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };
  const shownReason = info?.ok ? (info.reason ?? null) : reason;
  const canReapply = info?.ok === true && info.canReapply === true;
  return (
    <OrgBlockedNotice status="terminated" rejected rejectReason={shownReason} onLogout={onLogout}>
      {info === null && !error && <p className="mt-4 text-sm text-muted-foreground">회사 정보를 불러오는 중…</p>}
      {info && !info.ok && info.message && <p className="mt-4 text-sm text-muted-foreground">{info.message}</p>}
      {info?.ok && values && (
        <form className="mt-5 flex flex-col gap-y-4 text-left" data-testid="org-reapply-form" noValidate onSubmit={submit}>
          <h2 className="text-base font-semibold">{REAPPLY_TITLE}</h2>
          <CompanyFields pending={pending || !canReapply} slugLocked values={values} onChange={onChange} />
          {canReapply ? (
            <Button data-testid="org-reapply-submit" disabled={pending} type="submit">
              {pending ? "신청하는 중…" : "다시 신청"}
            </Button>
          ) : (
            <p className="text-sm font-medium" data-testid="org-reapply-wait">
              {info.waitMessage}
            </p>
          )}
        </form>
      )}
      {error && (
        <p className="mt-3 text-left text-sm text-destructive" data-testid="org-reapply-error" role="alert">
          {error}
        </p>
      )}
    </OrgBlockedNotice>
  );
};

// 반려 정보를 한 번 읽어 폼을 채운다 · 다시 신청에 성공하면 셸이 승인 대기로 바뀌어 레이아웃이 이 화면을 내린다.
const OrgRejectedScreen = ({ reason, onLogout }: { reason: string | null; onLogout?: () => void }) => {
  const [info, setInfo] = useState<ReapplyInfoResult | null>(null);
  const [values, setValues] = useState<WizardValues | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    let live = true;
    reapplyInfo()
      .then((result) => {
        if (!live) return;
        setInfo(result);
        if (result.ok && result.form) setValues(reapplyValuesOf(result.form));
      })
      .catch(() => live && setError(REAPPLY_LOAD_FAILED_MESSAGE));
    return () => {
      live = false;
    };
  }, []);

  const onSubmit = () => {
    if (!values || !info?.form) return;
    const problem = reapplyErrorOf(values);
    setError(problem);
    if (problem) return;
    const input = reapplyInputOf(values, info.form);
    startTransition(async () => {
      try {
        const result = await sendReapply(input);
        if (!result.ok) {
          setError(result.message);
          return;
        }
        toast.success(result.message);
      } catch {
        setError(REAPPLY_FAILED_MESSAGE);
      }
    });
  };

  return (
    <OrgRejectedView
      error={error}
      info={info}
      pending={pending}
      reason={reason}
      values={values}
      onChange={(patch) => setValues((prev) => (prev ? { ...prev, ...patch } : prev))}
      onLogout={onLogout}
      onSubmit={onSubmit}
    />
  );
};

export default OrgRejectedScreen;
